import React, { useState } from 'react';
import { errorAndStateParams } from '../../../../helpers/forms/errorAndStateParams';
import { ClientFieldsUpdate } from './ClientFieldsUpdate';
import { CommandeFieldsUpdate } from './CommandeFieldsUpdate';
import { updateCommande } from '../../../../REST/commande/updateCommande';
import { CommandeStateUpdate } from 'types/commande/formatUpdateCommande';
import { GET_FRAIS_PORT } from 'domain/commande/getFraisPort';
import AddProductFields from './AddProductFields';
import {
  calculTotalNoPort,
  getCommandelines,
  getHorsLycee
} from './handleChange';
import {
  Enum_Commande_Methodepaiement,
  Enum_Commande_Status
} from '../../../../graphql/generated/graphql';
import { sanitizeCalul } from '../../../../helpers/number/sanitizeCalul';
import { CommandeDef } from '../../../../types/commande/commande.def';
import { ProductDef } from '../../../../types/product/product';
import { ButtonWithText } from '../../../atoms/Buttons/ButtonWithText';
import { HeaderText } from '../../../atoms/Texts/HeaderText';
import emojis from '../../../../theme/emoji';
import { FeatureContainer } from '../../../molecules/Containers/FeatureContainer';
import { Divider } from '../../../atoms/Dividers/Divider';
import { ProductLinePanier } from '../../../molecules/Panier/ProductLinePanier';
import useStyle from './updateCommande.style';
import { BackButton } from '../../../molecules/Buttons/BackButton';
import { ContainerTwoThirds } from '../../../layouts/ContainerTwoThirds';
import { Modal } from '../../Modals/Modal';
import toast from 'react-hot-toast';
import { TOTAL_COMMANDE_A_PAYER } from '../../../../helpers/commande/totalCommandeAPayer';
import { formatUpdateCommande } from '../../../../helpers/commande/formatUpdateCommande';
import { GET_STATUS_FROM_PAYMENT_METHOD } from '../../../../helpers/commande/getStatutFromPaymentMethod';
import useGoToPage from '../../../../hooks/useGoToPage';
import pages from '../../../../routes/pages';
import { isUpdateFormValid } from '../../../../helpers/forms/formCommande';

export const initialState = (commande: CommandeDef): CommandeStateUpdate => {
  if (!commande) return null;
  const { client, commandelines, ...rest } = commande;
  return {
    client: {
      values: commande.client,
      errors: null,
      isValid: false
    },
    commande: rest,
    commandelines: commande.commandelines,
    initTotalNoPort: commande.totalttc,
    totalNoPort: commande.totalttc,
    prixTotal: commande.prixTotal,
    currentFraisPort: commande.fraislivraison,
    cmlToDelete: []
  };
};

const UpdateCommande = ({ commande }: { commande: CommandeDef }) => {
  const classes = useStyle();
  const goToCommande = useGoToPage(pages.COMMANDE_SINGLE_PAGE.path, {
    id: commande.id
  });
  const [cmdState, setCmdState] = useState<CommandeStateUpdate>(
    initialState(commande)
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const submit = async () => {
    setSubmitLoading(true);
    await updateCommande(formatUpdateCommande(cmdState));
    setSubmitLoading(false);
    goToCommande();
  };

  const [cancelLoading, setCancelLoading] = useState(false);
  const cancelOrder = async () => {
    setCancelLoading(true);
    await updateCommande(
      formatUpdateCommande({
        ...cmdState,
        commande: { ...cmdState.commande, status: Enum_Commande_Status.Annule }
      })
    );
    setCancelLoading(false);
  };

  const handleChange = (
    e: { target: { name: string; value: unknown } },
    type: 'client' | 'commandelines' | 'commande',
    i: number = null
  ): void => {
    const value = e.target.value;
    const name = e.target.name;
    let newState = cmdState;

    if (type === 'client') {
      const res = errorAndStateParams(name, value, cmdState[type]);
      const isHorlycee = getHorsLycee(
        name,
        value,
        cmdState.client.values.livraisonhorslycee
      );
      const totalNoPort = sanitizeCalul(
        calculTotalNoPort(cmdState.commandelines)
      );
      newState = {
        ...cmdState,
        [type]: { ...res },
        currentFraisPort: GET_FRAIS_PORT(totalNoPort, isHorlycee),
        totalNoPort,
        commande: {
          ...cmdState.commande,
          prixTotal: TOTAL_COMMANDE_A_PAYER(
            totalNoPort,
            isHorlycee,
            cmdState.commande.customerContributedToTheOng
          )
        },
        prixTotal: TOTAL_COMMANDE_A_PAYER(
          totalNoPort,
          isHorlycee,
          cmdState.commande.customerContributedToTheOng
        )
      };
    }
    if (type === 'commandelines') {
      if (
        cmdState?.commandelines &&
        cmdState.commandelines.length > 0 &&
        cmdState.commandelines.some(
          prod => prod.produit?.id === (value as ProductDef)?.id
        )
      ) {
        toast(
          'Le produit est déjà présent dans votre commande, veuillez éditer la quantité'
        );

        return;
      }
      if ((value as ProductDef)?.id) {
        toast.success((value as ProductDef).name + ' ajouté avec success');
      }
      newState = getCommandelines(name, value, cmdState, i);
    }
    if (type === 'commande') {
      if (name === 'methodepaiement') {
        newState = {
          ...cmdState,
          [type]: {
            ...cmdState[type],
            [name]: value as Enum_Commande_Methodepaiement,
            status: GET_STATUS_FROM_PAYMENT_METHOD(value)
          }
        };
      } else {
        newState = {
          ...cmdState,
          [type]: { ...cmdState[type], [name]: value }
        };
      }
      if (name === 'customerContributedToTheOng') {
        newState = {
          ...cmdState,
          [type]: {
            ...cmdState[type],
            [name]: value === 'true',
            prixTotal: TOTAL_COMMANDE_A_PAYER(
              sanitizeCalul(calculTotalNoPort(cmdState.commandelines)),
              getHorsLycee(
                name,
                value,
                cmdState.client.values.livraisonhorslycee
              ),
              value === 'true'
            )
          },
          prixTotal: TOTAL_COMMANDE_A_PAYER(
            sanitizeCalul(calculTotalNoPort(cmdState.commandelines)),
            getHorsLycee(
              name,
              value,
              cmdState.client.values.livraisonhorslycee
            ),
            value === 'true'
          )
        };
      }
    }
    setCmdState(newState);
  };

  const handleChangeQte = (value, i) => {
    const e = {
      target: {
        name: 'quantite',
        value: value
      }
    };
    handleChange(e, 'commandelines', i);
  };

  const handleChangeSelect = (value, name) => {
    const e = {
      target: {
        name: name,
        value: value
      }
    };
    handleChange(e, 'client');
  };

  const handleCheckSwitch = (value, name, type: 'client' | 'commande') => {
    const e = {
      target: {
        value: value.toString(),
        name: name
      }
    };
    handleChange(e, type);
  };

  return (
    <>
      <BackButton text="Revenir aux commandes" />
      <ContainerTwoThirds
        col1={
          <div>
            <ClientFieldsUpdate
              errors={cmdState.client.errors}
              client={cmdState.client.values}
              ngoContribution={cmdState.commande.customerContributedToTheOng}
              handleChangeSwitch={handleChangeSelect}
              handleChange={e => handleChange(e, 'client')}
              handleCheckSwitch={handleCheckSwitch}
            />
          </div>
        }
        col2={
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            <CommandeFieldsUpdate
              commande={cmdState.commande}
              handleChange={e => handleChange(e, 'commande')}
            />
            <div>
              <HeaderText
                text={'Panier prévisionnel'}
                emoji={emojis['ShoppingCart']}
              />
              <FeatureContainer>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}
                >
                  <p className={classes.paragraph}>Description produit</p>
                  <p className={classes.paragraph}>Sous Total</p>
                </div>
                <Divider />
                {cmdState.commandelines.map((cml, i) => (
                  <>
                    <ProductLinePanier
                      line={cml}
                      onClickViewMore={() => console.log('voir plus')}
                      editableQuantity={true}
                      defaultQuantity={cml.quantite}
                      onChangeQuantity={e => handleChangeQte(e, i)}
                      onClickTrash={() =>
                        handleChange(
                          { target: { name: 'suppr', value: null } },
                          'commandelines',
                          i
                        )
                      }
                    />
                    <Divider />
                  </>
                ))}
                <p className={classes.paragraph}>
                  {cmdState.commandelines.length} article(s)
                </p>
                <p className={classes.paragraph}>
                  Prix total du panier prévisionnal (SANS PORT et ONG):
                </p>
                <p className={classes.boldParagraph}>{cmdState.totalNoPort}€</p>
                <p className={classes.paragraph}>
                  Prix total du panier prévisionnal:
                </p>
                <p className={classes.boldParagraph}>{cmdState.prixTotal}€</p>
                <AddProductFields handleChange={handleChange} />
              </FeatureContainer>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                margin: '10px',
                width: '100%',
                justifyContent: 'center'
              }}
            >
              <ButtonWithText
                content={'Valider les modifications'}
                onClick={submit}
                loading={submitLoading}
                disabled={!isUpdateFormValid(cmdState.client)}
              />
              <Modal
                modalHead={'Annuler la commande'}
                triggerButton={
                  <ButtonWithText
                    content={'Annuler la commande'}
                    onClick={null}
                    disabled={commande.status === Enum_Commande_Status.Annule}
                    variant="danger"
                  />
                }
                actionButton={
                  <ButtonWithText
                    content={'Annuler la commande'}
                    onClick={cancelOrder}
                    variant="danger"
                    loading={cancelLoading}
                  />
                }
              >
                <p style={{ margin: 'auto' }}>
                  Souhaitez-vous annuler cette commande ?
                </p>
              </Modal>
            </div>
          </div>
        }
      />
    </>
  );
};

export default UpdateCommande;
